import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Mail, Phone, Lock, Eye, EyeOff, LogIn } from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";
import { useCustomerAuth } from "../../../context/CustomerAuthContext/CustomerAuthContext";

const isEmail = (v: string) => /\S+@\S+\.\S+/.test(v);
const isPhone = (v: string) => /^[0-9 +()-]{7,}$/.test(v);

const LoginPage: React.FC = () => {
  const { login, loading, token } = useCustomerAuth();
  const { theme } = useTheme();
  const nav = useNavigate();
  const loc = useLocation() as any;

  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [touched, setTouched] = useState<{ user?: boolean; pass?: boolean }>({});

  // Eğer zaten login ise bu sayfada bekletme → panel
  useEffect(() => {
    if (token) nav("/musteri/panel", { replace: true });
  }, [token, nav]);

  const userErr = useMemo(() => {
    if (!touched.user) return "";
    if (!user.trim()) return "Bu alan zorunludur.";
    if (!(isEmail(user) || isPhone(user))) return "Geçerli e-posta ya da telefon girin.";
    return "";
  }, [user, touched.user]);

  const passErr = useMemo(() => {
    if (!touched.pass) return "";
    if (!pass.trim()) return "Şifre zorunludur.";
    if (pass.length < 6) return "En az 6 karakter olmalı.";
    return "";
  }, [pass, touched.pass]);

  const doLogin = async () => {
    setTouched({ user: true, pass: true });
    setErr(null);
    if (userErr || passErr) return;

    const payload = isEmail(user) ? { Eposta: user.trim(), Sifre: pass } : { Telefon: user.trim(), Sifre: pass };

    const res = await login(payload as any);
    if (res.success) nav(loc?.state?.from ?? "/musteri/panel", { replace: true });
    else setErr(res.errorDetail || "Giriş başarısız.");
  };

  const onKeyDown = (e: React.KeyboardEvent) => { if (e.key === "Enter") doLogin(); };

  return (
    <div className="min-h-[70vh] grid place-items-center px-4 py-10"
    style={{
      backgroundColor: theme?.backgroundColor || "#f9fafb",
      color: theme?.textPrimary || "#111827",
    }}
    >
      <div className="w-full max-w-md" onKeyDown={onKeyDown}>
        <div className="rounded-t-2xl p-6" style={{ background: theme?.gradientHero ?? "linear-gradient(90deg,#0ea5e9,#22c55e)" }}>
          <h1 className=" text-2xl font-semibold">Giriş Yap</h1>
          <p className=" text-sm mt-1">Hesabınıza erişin ve siparişlerinizi yönetin.</p>
        </div>

        <div className="border rounded-b-2xl p-6 shadow-sm space-y-4" style={{ borderColor: "#E5E7EB" }}>
          <div>
            <label className="block text-sm mb-1">E-posta veya Telefon <span className="text-red-600">*</span></label>
            <div className={`flex items-center gap-2 border rounded-lg px-3 py-2 ${userErr ? "border-red-500" : "border-gray-300"}`}>
              {isEmail(user) ? <Mail size={18} className="" /> : <Phone size={18} className="" />}
              <input value={user} onChange={(e) => setUser(e.target.value)} onBlur={() => setTouched((t) => ({ ...t, user: true }))} className="w-full outline-none bg-transparent text-sm" placeholder="ornek@site.com / 0532xxxxxxx" />
            </div>
            {userErr && <p className="mt-1 text-xs text-red-600">{userErr}</p>}
          </div>

          <div>
            <label className="block text-sm mb-1">Şifre <span className="text-red-600">*</span></label>
            <div className={`flex items-center gap-2 border rounded-lg px-3 py-2 ${passErr ? "border-red-500" : "border-gray-300"}`}>
              <Lock size={18} className="" />
              <input type={show ? "text" : "password"} value={pass} onChange={(e) => setPass(e.target.value)} onBlur={() => setTouched((t) => ({ ...t, pass: true }))} placeholder="••••••••" className="w-full outline-none bg-transparent text-sm" />
              <button type="button" onClick={() => setShow((s) => !s)} className=" ">{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>
            </div>
            {passErr && <p className="mt-1 text-xs text-red-600">{passErr}</p>}
          </div>

          {err && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{err}</div>}

          <button type="button" onClick={doLogin} disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-lg  font-medium h-11" style={{ background: theme?.accent ?? "#22c55e" }}>
            <LogIn size={18} /> {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
          </button>

          <p className="text-center text-sm">
            Hesabın yok mu? <Link to="/musteri/kayit" className="underline" style={{ color: theme?.accent }}>Kayıt Ol</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
